/**
 * Utility Functions for IIIF Viewer Components
 *
 * This module provides shared utility functions used across the IIIF viewer
 * application. These functions handle common tasks like URL parsing, iframe
 * communication, DOM observation, and cross-browser compatibility.
 *
 * Categories:
 * - URL Parameter Handling
 * - Iframe Communication & Styling
 * - DOM Observation Utilities
 * - Cross-Browser Fullscreen Support
 */

// URL PARAMETER UTILITIES

/**
 * Extract URL parameter value by parameter name
 *
 * Uses the modern URLSearchParams API to safely parse query parameters
 * from the current page URL. Returns empty string as fallback to prevent
 * null/undefined issues in template binding.
 *
 * Example:
 * URL: "https://example.com/viewer?view_type=portret&debug=true"
 * getUrlParameter("view_type") → "portret"
 * getUrlParameter("missing") → ""
 *
 * @param {string} name - The parameter name to look for
 * @returns {string} Parameter value or empty string if not found
 */
export function getUrlParameter(name) {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(name) ?? "";
}

// IFRAME COMMUNICATION UTILITIES

/**
 * Handle parent iframe CSS classes for modal overlay states
 *
 * This function manages the communication between the IIIF viewer (running
 * inside an iframe) and its parent document. It applies/removes CSS classes
 * to create overlay effects when the viewer enters modal mode.
 *
 * Two-level styling approach:
 * 1. Parent document body: Gets overlay background and padding
 * 2. Specific iframe element: Gets modal-specific styling
 *
 * Cross-origin safety:
 * - Safely handles cross-origin iframe restrictions
 * - Gracefully fails if parent access is blocked
 * - Uses try-catch for iframe identification
 *
 * @param {string} action - Either 'add' or 'remove' to control class manipulation
 * @param {string} [className="iframe-partial-fullscreen"] - CSS class to apply to parent body
 */
export function handleParentIframeClasses(
    action,
    className = "iframe-partial-fullscreen"
) {
    // Early return if no parent document access
    if (!window.parent?.document) return;

    const parentDoc = window.parent.document;

    // Apply/remove class to parent document body
    if (action === "add") {
        parentDoc.body.classList.add(className);
    } else {
        parentDoc.body.classList.remove(className);
    }

    // Find and style the specific iframe containing this viewer
    const iframes = parentDoc.querySelectorAll("iframe");
    iframes.forEach((iframe) => {
        try {
            // Identify our iframe by comparing contentWindow
            if (iframe.contentWindow === window) {
                if (action === "add") {
                    iframe.classList.add("iiif-viewer-modal");
                    // Note: Height styling handled via CSS classes
                } else {
                    iframe.classList.remove("iiif-viewer-modal");
                }
            }
        } catch (e) {
            // Cross-origin access restrictions - silently continue
            // This is expected behavior for cross-origin iframes
        }
    });
}

// DOM OBSERVATION UTILITIES

/**
 * Create a reusable DOM observer for dynamic element detection
 *
 * Generic utility for watching the DOM for specific elements to appear.
 * Automatically disconnects after finding the target to prevent performance
 * issues from ongoing observations.
 *
 * Note: This is a general-purpose utility. The IIIF viewer uses a more
 * specific implementation in the useDOMObserver composable.
 *
 * Use cases:
 * - Waiting for third-party components to render
 * - Detecting dynamically injected content
 * - Integration with external libraries
 *
 * @param {string} selector - CSS selector to watch for
 * @param {Function} callback - Function called when element is found
 * @returns {MutationObserver} The observer instance for manual control
 */
export function createDOMObserver(selector, callback) {
    const observer = new MutationObserver(() => {
        if (document.querySelector(selector)) {
            callback();
            observer.disconnect(); // Auto-cleanup after success
        }
    });

    // Watch entire document body tree for changes
    observer.observe(document.body, {
        childList: true, // Watch for child element addition/removal
        subtree: true, // Watch entire subtree, not just direct children
    });

    return observer;
}

// CROSS-BROWSER FULLSCREEN UTILITIES

/**
 * Add cross-browser fullscreen change event listener
 *
 * The Fullscreen API has inconsistent vendor prefixes across browsers.
 * This utility normalizes the differences and provides a single callback
 * interface for fullscreen state changes.
 *
 * Browser Support:
 * - Standard: fullscreenchange, document.fullscreenElement
 * - WebKit: webkitfullscreenchange, document.webkitFullscreenElement
 * - Mozilla: mozfullscreenchange, document.mozFullScreenElement
 * - IE/Edge: MSFullscreenChange, document.msFullscreenElement
 *
 * Event Detection:
 * - Listens to all vendor-prefixed events simultaneously
 * - Checks all vendor-prefixed properties for current state
 * - Provides normalized boolean callback parameter
 *
 * Usage Example:
 * ```javascript
 * onFullscreenChange((isFullscreen) => {
 *   if (isFullscreen) {
 *     console.log("Entered fullscreen");
 *   } else {
 *     console.log("Exited fullscreen");
 *   }
 * });
 * ```
 *
 * @param {(isFullscreen: boolean) => void} callback - Called with fullscreen state
 */
export const onFullscreenChange = (callback) => {
    /**
     * Event handler that normalizes fullscreen state across browsers
     */
    const handler = () => {
        const el = document;

        // Check all vendor-prefixed fullscreen properties
        const isFullscreen = !!(
            (
                el.fullscreenElement || // Standard
                el.webkitFullscreenElement || // WebKit/Safari
                el.mozFullScreenElement || // Firefox
                el.msFullscreenElement
            ) // IE/Edge
        );

        callback(isFullscreen);
    };

    // Register handler for all vendor-prefixed fullscreen events
    const events = [
        "fullscreenchange", // Standard
        "webkitfullscreenchange", // WebKit/Safari
        "mozfullscreenchange", // Firefox
        "MSFullscreenChange", // IE/Edge
    ];

    events.forEach((event) => {
        document.addEventListener(event, handler);
    });
};
